import type {
  ElementSourceTag,
  InventionElementResponse,
} from "@patent-studio/api-types";
import { toneClasses } from "@/components/ui/tone";
import type { HighlightTone } from "../documents/HighlightLayer";

/**
 * What each source tag is called, what it means, and what it asks of the attorney.
 *
 * One table because the same four tags appear in three places: the chip on an
 * element's card, the Key panel that defines them, and the flag on an element
 * awaiting attention. Three private copies of the wording drift, and the whole
 * point of the tag is that it means the same thing wherever it is read.
 *
 * The definitions are the ones settled with the attorneys, not paraphrases of
 * them. `requiresConfirmation` is stated rather than inferred from the colour,
 * because the consequence of a tag is the part that must never be guessed at.
 */
export interface ElementTag {
  label: string;
  definition: string;
  /** Whether an element carrying this tag is one the attorney must stand behind. */
  requiresConfirmation: boolean;
  /** The chip's classes. Colour is never the only carrier: the label is text. */
  chipClass: string;
  /** The card's left edge and ground, which read as the same source at a glance. */
  cardClass: string;
  /** The provenance line's colour, as v0.28 tints it by the same source. */
  lineClass: string;
}

/**
 * Green is reserved. It is applied only to material traceable to the inventor's
 * own disclosure, and is never conferred by a confirmation or by an inference,
 * which is why the confirmed state is a second badge rather than a fifth tag.
 *
 * Unverified is red because it is the attorney's own declaration, on the add
 * form, that the element is in no document. The colour marks a choice they
 * made, never a search that failed.
 */
export const ELEMENT_TAGS: Record<ElementSourceTag, ElementTag> = {
  from_the_disclosure: {
    label: "From the disclosure",
    definition:
      "The inventor's own words, traceable to a specific paragraph. No action needed.",
    requiresConfirmation: false,
    chipClass: toneClasses("success", "surface"),
    cardClass: toneClasses("success", "edge"),
    lineClass: "text-muted-foreground",
  },
  from_supporting_notes: {
    label: "From supporting notes",
    definition:
      "Found in the notes you provided, but not in the disclosure documentation. Requires confirmation.",
    requiresConfirmation: true,
    chipClass: "border-border bg-secondary text-secondary-foreground",
    cardClass: "border-l-brand-mid bg-secondary/40",
    lineClass: "text-secondary-foreground",
  },
  ai_framing: {
    label: "AI framing",
    definition:
      "A relationship I inferred between parts referenced in the disclosure documentation. Requires confirmation.",
    requiresConfirmation: true,
    chipClass: toneClasses("warning", "surface"),
    cardClass: `${toneClasses("warning", "edge")} ${toneClasses("warning", "ground")}`,
    lineClass: toneClasses("warning", "words"),
  },
  unverified: {
    label: "Unverified",
    definition:
      "Not found in the disclosure documentation or the supporting notes. Requires confirmation.",
    requiresConfirmation: true,
    chipClass: toneClasses("destructive", "surface"),
    cardClass: `${toneClasses("destructive", "edge")} ${toneClasses("destructive", "ground")}`,
    lineClass: toneClasses("destructive", "words"),
  },
};

/** The tags in the order the Key reads them: grounded first, then what needs work. */
export const TAG_ORDER: readonly ElementSourceTag[] = [
  "from_the_disclosure",
  "from_supporting_notes",
  "ai_framing",
  "unverified",
];

/**
 * A tag the served bundle has never heard of. One plain chip rather than a throw
 * inside the list's map, which would unmount every element on the page.
 */
const UNKNOWN_TAG: ElementTag = {
  label: "Unrecognised source",
  definition:
    "This element carries a source this version of the page does not know. Reload to pick up the current version.",
  requiresConfirmation: true,
  chipClass: toneClasses("neutral", "surface"),
  cardClass: toneClasses("neutral", "edge"),
  lineClass: "text-muted-foreground",
};

/**
 * Read through a map, not by index. The exhaustive table forces a new tag to be
 * given an entry, and makes an indexed read look infallible when it is not: the
 * tag comes from a server that can be ahead of this bundle. `get` says so.
 */
const BY_TAG: ReadonlyMap<string, ElementTag> = new Map(
  Object.entries(ELEMENT_TAGS),
);

export function tagOf(tag: ElementSourceTag): ElementTag {
  return BY_TAG.get(tag) ?? UNKNOWN_TAG;
}

/**
 * Whether the element is one the attorney has still to answer.
 *
 * The state the server states, never the count derived from it: the two agree
 * today because the count follows the state, and only one of them is the fact.
 */
export function isAwaitingAttention(
  element: InventionElementResponse,
): boolean {
  return element.state === "requires_attention";
}

/**
 * How an element's cited passages read in the source document: referenced,
 * once the element is grounded or the attorney has confirmed it; cited and
 * awaiting their word until then.
 */
export function highlightToneOf(
  element: InventionElementResponse,
): HighlightTone {
  return element.confirmed_at !== null ||
    !tagOf(element.source_tag).requiresConfirmation
    ? "referenced"
    : "cited";
}
